import { memo } from 'react';
import { Alert, Box, Button, Modal, SpaceBetween, Toggle } from '@amzn/awsui-components-core';

import { PAGE_TITLE_MAX_LENGTH, PAGE_TITLE_MIN_LENGTH } from '../v2-common/InlineValidations';
import TitleInput from '../v2-common/TitleInput';
import { ContentPageTree } from './ContentPageTree';
import { AddNewPageButtonViewModel } from './AddNewPageButtonViewModel';

interface AddNewPageModalProps {
  readonly vm: AddNewPageButtonViewModel;
}

/** Modal dialog for adding a new page to a Guide document. */
const AddNewPageModal = memo(function AddNewPageModal({ vm }: AddNewPageModalProps) {
  return (
    <Modal
      visible
      header="Add New Page"
      onDismiss={vm.dismissModal}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={vm.dismissModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!vm.isAddEnabled}
              loading={vm.isAddingPage}
              onClick={vm.confirmAdd}
            >
              Add
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <TitleInput
          initialTitle={vm.title}
          minLength={PAGE_TITLE_MIN_LENGTH}
          maxLength={PAGE_TITLE_MAX_LENGTH}
          onTitleChanged={vm.updateTitle}
          autoFocus
        />
        <Toggle checked={vm.isParentPageSelectionExpanded} onChange={vm.toggleParentPageSelection}>
          Select a parent page{' '}
          <Box display="inline" color="text-status-inactive">
            (optional)
          </Box>
        </Toggle>
        {vm.isParentPageSelectionExpanded && vm.pages.length > 0 ? (
          <Box>
            <ContentPageTree
              pages={vm.pages}
              selectedNode={vm.parentPage}
              onSelectedNodeChainChanged={vm.updateParentPage}
            />
          </Box>
        ) : null}
        {vm.parentPageError ? <Box color="text-status-error">{vm.parentPageError}</Box> : null}
        {vm.addErrorMessage !== undefined && <Alert type="error">{vm.addErrorMessage}</Alert>}
      </SpaceBetween>
    </Modal>
  );
});

export default AddNewPageModal;
